import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import { ChevronDown } from 'lucide-react';

export const HeroSection = () => {
  const { t } = useLanguage();
  const { isDark } = useTheme();

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background glow effect */}
      <div className="hero-glow" />
      
      {/* Background image - Northern Lights */}
      <div 
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: `url('https://customer-assets.emergentagent.com/job_cyber-lime-studio/artifacts/778u1h7t_image.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      
      {/* Overlay that adapts to theme */}
      <div 
        className="absolute inset-0 z-0 transition-colors duration-300"
        style={{
          background: isDark 
            ? 'linear-gradient(to bottom, rgba(15, 23, 42, 0.4) 0%, rgba(15, 23, 42, 0.7) 100%)'
            : 'linear-gradient(to bottom, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.2) 100%)',
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-32">
        <div className="max-w-4xl">
          {/* Main headline */}
          <h1
            data-testid="hero-headline"
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight text-foreground fade-in-up"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            {t('heroHeadline')}
          </h1>

          {/* Sub-headline */}
          <p
            data-testid="hero-subheadline"
            className={`mt-6 text-lg md:text-xl max-w-2xl leading-relaxed fade-in-up stagger-1 ${isDark ? 'text-muted-foreground' : 'text-white/80'}`}
          >
            {t('heroSubheadline')}
          </p>

          {/* CTA Buttons */}
          <div className="mt-10 flex flex-col sm:flex-row gap-4 fade-in-up stagger-2">
            <button
              onClick={() => scrollToSection('contact')}
              data-testid="cta-primary"
              className="px-8 py-4 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-semibold text-lg btn-glow transition-all"
            >
              {t('ctaPrimary')}
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              data-testid="cta-secondary"
              className={`px-8 py-4 bg-transparent border rounded-full font-semibold text-lg transition-all backdrop-blur-sm ${isDark ? 'border-border hover:border-foreground/50 text-foreground' : 'border-white/50 hover:border-white text-white'}`}
            >
              {t('ctaSecondary')}
            </button>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 scroll-indicator">
          <button
            onClick={() => scrollToSection('problems')}
            data-testid="scroll-indicator"
            className="p-2 rounded-full glass hover:border-primary/30 transition-all"
            aria-label="Scroll down"
          >
            <ChevronDown size={24} className={isDark ? 'text-muted-foreground' : 'text-white/70'} />
          </button>
        </div>
      </div>
    </section>
  );
};
